import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Briefcase, Loader2, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useBrand, useSiteSection, waLink } from "@/hooks/useSiteContent";

export const Route = createFileRoute("/trabalhe-conosco")({
  head: () => ({
    meta: [
      { title: "Trabalhe Conosco — Vagas na Contato Internet" },
      {
        name: "description",
        content:
          "Faça parte do time da Contato Internet. Confira as vagas abertas e envie seu currículo para trabalhar com tecnologia no Sul de SC.",
      },
      { property: "og:title", content: "Trabalhe Conosco — Contato Internet" },
      { property: "og:description", content: "Vagas abertas e envio de currículo." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: JobsPage,
});

function JobsPage() {
  const jobs = useSiteSection("jobs");
  const brand = useBrand();
  const [form, setForm] = useState({
    name: "",
    role: "",
    city: "",
    phone: "",
    email: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const openings = (jobs.items ?? []).filter((j) => j.role);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSending(true);
    const { error } = await supabase.from("job_applications").insert({
      name: form.name,
      role: form.role || null,
      city: form.city || null,
      phone: form.phone || null,
      email: form.email || null,
      message: form.message || null,
    });
    setSending(false);
    if (error) {
      toast.error("Não conseguimos enviar sua candidatura. Tente novamente.");
      return;
    }
    setSent(true);
    toast.success("Candidatura enviada! Nosso time de RH vai analisar.");
  }

  function sendWhatsApp() {
    const text = [
      "Olá! Quero me candidatar a uma vaga na Contato.",
      `Nome: ${form.name}`,
      `Vaga: ${form.role}`,
      `Cidade: ${form.city}`,
      form.message && `Sobre mim: ${form.message}`,
    ]
      .filter(Boolean)
      .join("\n");
    window.open(waLink(brand.whatsapp, text), "_blank", "noreferrer");
  }

  return (
    <>
      <section className="bg-[linear-gradient(110deg,var(--brand-sky-deep),var(--brand-deep))] py-14 text-brand-deep-foreground sm:py-20">
        <div className="mx-auto max-w-7xl px-5">
          <p className="text-[0.7rem] font-extrabold uppercase tracking-[0.2em] text-brand-yellow">
            Carreiras
          </p>
          <h1 className="mt-2 text-3xl font-extrabold uppercase leading-tight sm:text-4xl">
            {jobs.title}
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-brand-sky sm:text-base">{jobs.subtitle}</p>
        </div>
      </section>

      <section className="bg-background py-12 sm:py-16">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <h2 className="text-xl font-extrabold uppercase text-brand-deep">Vagas abertas</h2>
            <ul className="mt-6 space-y-4">
              {openings.length === 0 && (
                <li className="rounded-2xl border border-dashed border-border p-6 text-sm text-muted-foreground">
                  Nenhuma vaga aberta no momento — envie seu currículo para o nosso banco de
                  talentos.
                </li>
              )}
              {openings.map((job, i) => (
                <li
                  key={`${job.role}-${job.location}-${i}`}
                  className="rounded-3xl border border-border bg-card p-6 transition hover:shadow-md"
                >
                  <div className="flex items-start gap-3">
                    <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-secondary text-primary">
                      <Briefcase className="size-5" strokeWidth={2.4} />
                    </span>
                    <div className="min-w-0">
                      <h3 className="text-base font-extrabold text-brand-deep">{job.role}</h3>
                      <p className="mt-0.5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-brand-sky">
                        <MapPin className="size-3.5" />
                        {job.location}
                      </p>
                      {job.url ? (
                        <a
                          href={job.url}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-2 inline-flex text-sm font-extrabold text-primary hover:underline"
                        >
                          Ver detalhes da vaga
                        </a>
                      ) : null}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <form
            onSubmit={submit}
            className="h-fit rounded-[2rem] border border-border bg-card p-6 sm:p-8"
          >
            <h2 className="text-lg font-extrabold uppercase text-brand-deep">
              {jobs.talentTitle || "Envie seu currículo"}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Preencha os dados: sua candidatura vai direto para o nosso RH.
            </p>
            <div className="mt-5 space-y-3">
              {[
                { key: "name", label: "Seu nome", type: "text" },
                { key: "role", label: "Vaga de interesse", type: "text" },
                { key: "city", label: "Cidade", type: "text" },
                { key: "phone", label: "Telefone / WhatsApp", type: "tel" },
                { key: "email", label: "E-mail", type: "email" },
              ].map((f) => (
                <label key={f.key} className="block">
                  <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                    {f.label}
                  </span>
                  <input
                    type={f.type}
                    required={f.key === "name" || f.key === "phone"}
                    value={form[f.key as keyof typeof form]}
                    onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                    className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary"
                  />
                </label>
              ))}
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  Conte um pouco sobre você
                </span>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm((s) => ({ ...s, message: e.target.value }))}
                  className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary"
                />
              </label>
            </div>
            <button
              type="submit"
              disabled={sending}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-lime px-6 py-3.5 text-xs font-extrabold uppercase tracking-wide text-brand-deep transition-transform hover:-translate-y-0.5 disabled:opacity-60"
            >
              {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
              {sending ? "Enviando..." : "Enviar candidatura"}
            </button>
            {sent ? (
              <div className="mt-3 rounded-2xl bg-secondary p-3 text-center text-xs text-brand-deep">
                Recebemos sua candidatura! Se quiser, fale também pelo WhatsApp.
                <button
                  type="button"
                  onClick={sendWhatsApp}
                  className="mt-2 block w-full rounded-full border border-brand-deep/20 px-4 py-2 font-extrabold uppercase tracking-wide hover:bg-brand-deep/5"
                >
                  Falar no WhatsApp
                </button>
              </div>
            ) : null}
            {jobs.talentText ? (
              <p className="mt-3 text-center text-xs text-muted-foreground">{jobs.talentText}</p>
            ) : null}
          </form>
        </div>
      </section>
    </>
  );
}
